'use client';
import React from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

export default function LifeSciencesCaseStudy() {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-10" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(43,98,239,0.06), transparent, rgb(8,11,18))' }} />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-8">
          <Link href="/case-studies" className="inline-flex items-center gap-2 text-cobalt text-sm font-medium mb-8 hover:text-cobalt-glow transition-colors">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m15 18-6-6 6-6" />
            </svg>
            Back to Case Studies
          </Link>
          <span className="text-cobalt font-mono text-sm tracking-widest uppercase mb-4 block">Life Sciences</span>
          <h1 className="font-display font-bold text-4xl lg:text-5xl text-silver-text leading-tight mb-6 max-w-4xl">
            Transforming Life Science eLearning with AI-Based Multilingual Localization
          </h1>
          <p className="text-lg text-silver-muted max-w-2xl leading-relaxed mb-10">
            AI-led production with native-speaker review for regulated eLearning content in 12 languages - 60% faster delivery and 50% cost reduction without sacrificing scientific accuracy.
          </p>
          <div className="flex flex-wrap gap-6">
            {[
              { value: '60% Faster', label: 'Timeline Reduction' },
              { value: '50% Savings', label: 'Cost Reduction' },
              { value: '12', label: 'Languages' },
              { value: 'Dual-Stage', label: 'QA Process' },
            ]?.map((stat) => (
              <div key={stat?.label} className="glow-card rounded-xl px-6 py-4 text-center min-w-[120px]">
                <div className="text-xl font-bold text-cobalt-glow">{stat?.value}</div>
                <div className="text-xs text-silver-muted mt-1">{stat?.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Content */}
      <section className="py-16 lg:py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-12">

              {/* Challenge */}
              <div>
                <h2 className="font-display font-bold text-2xl text-silver-text mb-5 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-cobalt/20 border border-cobalt/30 flex items-center justify-center text-cobalt-glow text-sm font-mono">01</span>
                  The Challenge
                </h2>
                <p className="text-silver-muted leading-relaxed mb-4">
                  Sify Digital partnered with Into23 to localize a comprehensive Life Science eLearning course into 12 languages. The program needed translation and professionally narrated training content while compressing timelines and controlling cost - without sacrificing scientific accuracy or regulatory compliance.
                </p>
                <p className="text-silver-muted leading-relaxed">
                  Key challenges included reducing project timelines by 60% without quality loss, achieving 50% cost reduction, ensuring correct pronunciation of complex scientific terms, and maintaining consistent quality across all 12 languages.
                </p>
              </div>

              {/* Deliverables */}
              <div>
                <h2 className="font-display font-bold text-2xl text-silver-text mb-5 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-cobalt/20 border border-cobalt/30 flex items-center justify-center text-cobalt-glow text-sm font-mono">02</span>
                  Deliverables
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { title: 'Linguistically Accurate Translations', desc: 'Domain-specific accuracy with native linguist editing of AI-generated drafts' },
                    { title: 'AI Voice Narration', desc: 'AI-generated audio narration with linguist-led pronunciation correction for scientific terms' },
                    { title: 'Uncompressed Audio Files', desc: 'Mono MP3 format audio files ready for eLearning platform integration' },
                    { title: 'Dual-Stage QA', desc: 'Two-tier quality assurance covering both linguistic accuracy and audio compliance' },
                  ]?.map((item) => (
                    <div key={item?.title} className="glow-card rounded-xl p-5">
                      <h3 className="font-semibold text-silver-text text-sm mb-1">{item?.title}</h3>
                      <p className="text-xs text-silver-muted">{item?.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Methodology */}
              <div>
                <h2 className="font-display font-bold text-2xl text-silver-text mb-5 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-cobalt/20 border border-cobalt/30 flex items-center justify-center text-cobalt-glow text-sm font-mono">03</span>
                  Methodology
                </h2>
                <p className="text-silver-muted leading-relaxed mb-6">
                  Into23 deployed a hybrid AI and human workflow purpose-built for regulated eLearning content, combining speed with scientific precision.
                </p>
                <div className="space-y-4">
                  {[
                    { step: '1', title: 'AI Translation + Native Linguist Review', desc: 'AI-generated initial translations refined by native-speaking linguists with Life Sciences domain expertise.' },
                    { step: '2', title: 'AI Voice Synthesis', desc: 'AI voice narration generated for all 12 languages, with linguists reviewing and correcting pronunciation of scientific terminology.' },
                    { step: '3', title: 'Multi-Tier Quality Control', desc: 'Dual-stage QA covering linguistic accuracy, audio quality, and compliance with Life Sciences standards.' },
                    { step: '4', title: 'Batch-Based Project Management', desc: 'Structured batch delivery with secure file sharing, enabling parallel processing across all language pairs.' },
                  ]?.map((item) => (
                    <div key={item?.step} className="flex gap-4 p-5 glow-card rounded-xl">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cobalt/20 border border-cobalt/30 flex items-center justify-center text-cobalt-glow text-sm font-bold">{item?.step}</div>
                      <div>
                        <h3 className="font-semibold text-silver-text text-sm mb-1">{item?.title}</h3>
                        <p className="text-xs text-silver-muted leading-relaxed">{item?.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Languages */}
              <div>
                <h2 className="font-display font-bold text-2xl text-silver-text mb-5 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-cobalt/20 border border-cobalt/30 flex items-center justify-center text-cobalt-glow text-sm font-mono">04</span>
                  Languages Delivered
                </h2>
                <div className="flex flex-wrap gap-2">
                  {['French', 'German', 'Spanish (European)', 'Italian', 'Russian', 'Dutch', 'Brazilian Portuguese', 'Indonesian', 'Japanese', 'Korean', 'Chinese (Simplified)', 'Arabic']?.map((lang) => (
                    <span key={lang} className="px-3 py-1.5 rounded-lg bg-cobalt/10 border border-cobalt/20 text-cobalt-glow text-xs font-mono">{lang}</span>
                  ))}
                </div>
              </div>

              {/* Results */}
              <div>
                <h2 className="font-display font-bold text-2xl text-silver-text mb-5 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-cobalt/20 border border-cobalt/30 flex items-center justify-center text-cobalt-glow text-sm font-mono">05</span>
                  Results
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { metric: '60% faster completion', detail: 'Compared to traditional localization workflows' },
                    { metric: '50% cost savings', detail: 'Through AI-assisted production with human oversight' },
                    { metric: '12 languages delivered', detail: 'Fully localized and professionally narrated modules' },
                    { metric: 'Enhanced global reach', detail: 'Compliance maintained across all regulated markets' },
                  ]?.map((r) => (
                    <div key={r?.metric} className="glow-card rounded-xl p-5 border-l-2 border-cobalt/40">
                      <div className="font-semibold text-cobalt-glow text-sm mb-1">{r?.metric}</div>
                      <div className="text-xs text-silver-muted">{r?.detail}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="glow-card rounded-xl p-6">
                <h3 className="font-semibold text-silver-text mb-4">Project Details</h3>
                <dl className="space-y-3 text-sm">
                  <div><dt className="text-silver-muted text-xs uppercase tracking-wider mb-1">Industry</dt><dd className="text-silver-text">Life Sciences</dd></div>
                  <div><dt className="text-silver-muted text-xs uppercase tracking-wider mb-1">Client</dt><dd className="text-silver-text">Sify Digital</dd></div>
                  <div><dt className="text-silver-muted text-xs uppercase tracking-wider mb-1">Content Type</dt><dd className="text-silver-text">Regulated eLearning</dd></div>
                  <div><dt className="text-silver-muted text-xs uppercase tracking-wider mb-1">Languages</dt><dd className="text-silver-text">12 languages</dd></div>
                  <div><dt className="text-silver-muted text-xs uppercase tracking-wider mb-1">Approach</dt><dd className="text-silver-text">AI translation + AI voice + human review</dd></div>
                </dl>
              </div>

              <div className="glow-card rounded-xl p-6">
                <h3 className="font-semibold text-silver-text mb-3">Key Capabilities Used</h3>
                <ul className="space-y-2 text-sm text-silver-muted">
                  {['AI translation', 'Native linguist editing', 'AI voice synthesis', 'Pronunciation QA', 'Dual-stage quality control', 'Batch project management', 'Secure file delivery']?.map((cap) => (
                    <li key={cap} className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-cobalt-glow flex-shrink-0" />
                      {cap}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="glow-card rounded-xl p-6">
                <h3 className="font-semibold text-silver-text mb-3">Ready to Scale?</h3>
                <p className="text-sm text-silver-muted mb-4">Talk to our team about your eLearning localization program.</p>
                <Link href="/get-a-quote">
                  <span className="block text-center px-4 py-3 bg-cobalt hover:bg-cobalt-glow text-white text-sm font-semibold rounded-lg transition-all">
                    Get a Quote
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
